/**
 * AgentRoleDialog — ★ Fase 59: promozione/declassamento di un agente.
 *
 * L'admin sceglie il nuovo ruolo operativo (Affiliato / Associato / Senior /
 * Co-CEO / CEO / Community). Se il percorso training cambia, il backend AZZERA
 * il training e riattiva il blocco finché il nuovo percorso non è completato.
 *
 *  • Affiliato        → POST /api/agents/{id}/demote-to-affiliate
 *  • Tutti gli altri  → POST /api/agents/{id}/role  { role }
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { api, getCrmTenant } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, UserCog, GraduationCap, AlertTriangle } from 'lucide-react';

// ★ Fase 32 — etichette ruolo localizzate a runtime (getter → lingua attiva)
export const ROLE_LABELS = {
  get ceo() { return 'CEO'; },
  get co_ceo() { return 'Co-CEO'; },
  get senior_associate() { return i18n.t('roles.senior', 'Senior'); },
  get associate_manager() { return i18n.t('roles.associate', 'Associato'); },
  get community() { return 'Community'; },
  get communication() { return 'Community'; },
  get sales_director() { return i18n.t('roles.salesDirector', 'Direttrice Commerciale'); },
  get commerciale() { return i18n.t('roles.commercial', 'Commerciale'); },
  get agent() { return i18n.t('roles.agent', 'Agente'); },
  get affiliate() { return i18n.t('roles.affiliate', 'Affiliato'); },
};

const ROLE_OPTIONS = [
  { id: 'affiliate', labelKey: 'roleDialog.affiliateLabel', label: 'Affiliato (esterno)', descKey: 'roleDialog.affiliateDesc', desc: 'Solo link referral e dashboard affiliato' },
  { id: 'associate_manager', labelKey: 'roles.associate', label: 'Associato', descKey: 'roleDialog.associateDesc', desc: 'Qualifica i lead e li passa al Senior' },
  { id: 'senior_associate', labelKey: 'roles.senior', label: 'Senior', descKey: 'roleDialog.seniorDesc', desc: 'Show, proposte e chiusura' },
  { id: 'co_ceo', label: 'Co-CEO', descKey: 'roleDialog.coCeoDesc', desc: 'Direzione — percorso Senior' },
  { id: 'ceo', label: 'CEO', descKey: 'roleDialog.ceoDesc', desc: 'Direzione — percorso Senior' },
  { id: 'community', label: 'Community', descKey: 'roleDialog.communityDesc', desc: 'Staff — training non obbligatorio' },
];

export function AgentRoleDialog({ agent, open, onClose, onChanged }) {
  const { t } = useTranslation();
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  // ★ 2026-09-04 (Fase 28) — CRM IT: il ruolo "Associato" non esiste più
  const isItTenant = getCrmTenant() === 'it';
  const roleOptions = ROLE_OPTIONS.filter(
    (r) => !(isItTenant && r.id === 'associate_manager'),
  );

  useEffect(() => {
    if (open) setNewRole('');
  }, [open, agent?.id]);

  if (!agent) return null;
  const currentLabel = ROLE_LABELS[agent.role] || agent.role || t('roles.agent', 'Agente');
  const isDemoteToAffiliate = newRole === 'affiliate';
  const changed = newRole && newRole !== agent.role;

  const confirm = async () => {
    if (!changed) return;
    setSaving(true);
    try {
      let data;
      if (isDemoteToAffiliate) {
        ({ data } = await api.post(`/agents/${agent.id}/demote-to-affiliate`));
        toast.success(t('roleDialog.nowAffiliate', { defaultValue: '{{name}} è ora Affiliato', name: agent.name }), {
          description: t('roleDialog.nowAffiliateDesc', { defaultValue: 'Link referral: {{link}} — training azzerato: dovrà completare il Percorso Affiliato.', link: data.link }),
        });
      } else {
        ({ data } = await api.post(`/agents/${agent.id}/role`, { role: newRole }));
        const label = ROLE_LABELS[newRole] || newRole;
        toast.success(t('roleDialog.nowRole', { defaultValue: '{{name}} è ora {{label}}', name: agent.name, label }), {
          description: data.training_reset
            ? t('roleDialog.trainingResetDesc', 'Training azzerato: dovrà completare il percorso del nuovo ruolo (blocco riattivato).')
            : t('roleDialog.noResetDesc', 'Il percorso training non cambia: nessun reset necessario.'),
        });
      }
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('roleDialog.changeError', 'Errore cambio ruolo'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="agent-role-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-[hsl(var(--primary))]" /> {t('roleDialog.title', 'Cambia ruolo')} · {agent.name}
          </DialogTitle>
          <DialogDescription>
            {t('roleDialog.currentRole', 'Ruolo attuale:')} <b>{currentLabel}</b>. {t('roleDialog.desc', 'Scegli il nuovo ruolo operativo: promozioni e declassamenti aggiornano automaticamente permessi e training.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger data-testid="agent-role-select">
              <SelectValue placeholder={t('roleDialog.selectPlaceholder', 'Seleziona il nuovo ruolo…')} />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((r) => (
                <SelectItem key={r.id} value={r.id} disabled={r.id === agent.role} data-testid={`agent-role-option-${r.id}`}>
                  <span className="font-medium">{r.labelKey ? t(r.labelKey, r.label) : r.label}</span>
                  <span className="text-muted-foreground text-xs"> — {r.descKey ? t(r.descKey, r.desc) : r.desc}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {changed && (
            <div
              className="flex items-start gap-2 rounded-md border border-[#E4C88A] bg-[#FBF3DF] p-3 text-xs text-[#6A4B00]"
              data-testid="agent-role-training-warning"
            >
              <GraduationCap className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <b>{t('roleDialog.trainingWarnTitle', 'Training azzerato al cambio percorso.')}</b> {t('roleDialog.trainingWarnDesc', 'Se il nuovo ruolo prevede un percorso diverso, le missioni ripartono da zero e il blocco resta attivo finché non completa il nuovo percorso e ottiene il Certificato HCF.')}
              </span>
            </div>
          )}

          {isDemoteToAffiliate && (
            <div
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
              data-testid="agent-role-affiliate-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <b>{t('roleDialog.affiliateWarnTitle', 'Declassamento ad Affiliato:')}</b> {t('roleDialog.affiliateWarnDesc', 'la scheda agente viene disattivata, l\u2019accesso passa alla dashboard affiliato (stesse credenziali) e viene creato/riattivato il suo link referral personale.')}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="agent-role-cancel">
            {t('roleDialog.cancel', 'Annulla')}
          </Button>
          <Button
            onClick={confirm}
            disabled={!changed || saving}
            data-testid="agent-role-confirm"
            className={isDemoteToAffiliate
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]'}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isDemoteToAffiliate ? t('roleDialog.demoteBtn', 'Declassa ad Affiliato') : t('roleDialog.confirmBtn', 'Conferma nuovo ruolo')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
